/**
 * WidgetConfigDrawer — panneau latéral de configuration d'un widget pour le
 * builder de rapports Orion (Phase 2a).
 *
 * Porté depuis Metrix (`frontend/src/features/reports/WidgetConfigDrawer.tsx`)
 * pour la structure (drawer latéral, onglets Config/Style), mais adapté aux
 * endpoints et à la stack Orion :
 * - Metrix sépare les sources GA4/Ads (`dashboardApi.getSources`) et utilise
 *   react-query + zod. Orion n'a pas react-query ici (voir `useWidgetData.ts`
 *   et `DataSources.tsx` qui font du `fetch` simple) : ce composant suit le
 *   même pattern (fetch + useState/useEffect, pas de nouvelle dépendance).
 * - Une seule route Orion liste toutes les sources d'un client
 *   (`GET /api/data-sources/clients/:clientId/data-sources`), pas de
 *   distinction GA4/Ads séparée côté API : le typage vient du champ `type`
 *   de chaque `DataSource`.
 * - Le catalogue métriques/dimensions vient de
 *   `GET /api/query/metadata/:sourceType` (registry backend), pas des
 *   catalogues statiques `ga4.ts`/`ads.ts` de Metrix — Orion n'a pas
 *   (encore) ces fichiers, donc pas de fallback statique ici.
 * - Pas de champs calculés (`calculatedFieldsApi`) ni de compatibilité GA4
 *   dynamique (`checkCompatibility`) : hors scope Ph2a T12.
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { Widget, WidgetConfig, WidgetFilter } from './schemas';
import { defaultOperatorFor, operatorsFor, operatorOptionValue, resolveOperatorOption } from './filterOperators';
import { needsDataSource, needsTarget, hasTopN, isImageWidget, isRichTextWidget } from './widgetOptions';
import { uploadImage, fetchDimensionValues } from './reportsApi';
import { MAP_SCOPES } from './widgets/MapWidget';
import { BASEMAPS, DEFAULT_BASEMAP } from './widgets/basemaps';
import StyleTab from './config/StyleTab';
import { listFields } from '../calculated-fields/calculatedFieldsApi';
import type { CalcField } from '../calculated-fields/types';
import CalculatedFieldsManager from '../calculated-fields/CalculatedFieldsManager';

type Tab = 'config' | 'style';

/** Une source de données brute renvoyée par l'API Orion. */
interface DataSource {
  id: number;
  type: string;
  config?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Un champ (métrique ou dimension) du catalogue renvoyé par /api/query/metadata/:sourceType. */
interface CatalogField {
  key: string;
  label?: string;
  /** Dimension temporelle (`date`, `yearMonth`, `week`…) : jamais proposée comme dimension. */
  time?: boolean;
  [key: string]: unknown;
}

/** Champ du catalogue, natif ou calculé (flag `calculated` pour le badge fx dans les selects). */
type MergedField = CatalogField & { calculated?: boolean };

/** Libellés FR des granularités renvoyées par le registry. */
const GRANULARITY_LABELS: Record<string, string> = {
  day: 'Jour',
  week: 'Semaine',
  month: 'Mois',
  quarter: 'Trimestre',
};

/** Types de widgets dont l'axe X est temporel : la granularité y est le contrôle temps principal. */
const TIME_SERIES_WIDGETS = ['line_chart', 'bar_chart'];

/**
 * Widgets où dimension et granularité sont mutuellement exclusives comme axe X : le
 * dimension EST l'axe (une barre par valeur), combiner les deux ferait le produit
 * cartésien des deux (30 dates × 3 canaux) sur un axe qui n'a de sens que pour l'un
 * des deux. `line_chart`/`area_chart` n'en font pas partie : la dimension y sert de
 * segmentation en séries (une courbe par canal), l'axe X restant le temps — cf.
 * `context/specs/2026-08-04-line-chart-canal-serie.md`.
 */
const EXCLUSIVE_TIME_OR_DIMENSION_WIDGETS = ['bar_chart'];

/**
 * Widgets catégoriels capables de descendre d'un niveau au clic (cf. `useDrillDown`).
 * Sans ce contrôle dans le drawer, `display.drill_dimensions` resterait vide et le
 * drill-down serait inaccessible à l'utilisateur.
 */
const DRILLDOWN_WIDGETS = ['bar_chart', 'table', 'top_list', 'donut_pie', 'treemap', 'map'];

/** Libellés FR des cartes disponibles dans `public/maps/` (cf. `resolveMapName`). */
const MAP_SCOPE_LABELS: Record<(typeof MAP_SCOPES)[number], string> = {
  world: 'Monde',
  france: 'France (régions)',
  'france-departments': 'France (départements)',
};

interface Metadata {
  source_type: string;
  metrics: CatalogField[];
  dimensions: CatalogField[];
  granularities: string[];
}

export interface WidgetConfigDrawerProps {
  clientId: number;
  /** Widget sélectionné ; `null` → drawer fermé. */
  widget: Widget | null;
  /** Merge & remonte la config mise à jour. */
  onChange: (widgetId: string, config: WidgetConfig) => void;
  onClose: () => void;
}

/** Libellé lisible d'une source : type + nom de la propriété/compte (jamais l'ID brut).
 *  Les clés réelles stockées côté back sont en snake_case (`display_name`, `property_id`). */
function sourceLabel(source: DataSource): string {
  const typeLabel = source.type === 'ga4' ? 'GA4' : source.type === 'google_ads' ? 'Google Ads' : source.type;
  const cfg = (source.config ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const readable =
    str(cfg.display_name) || str(cfg.displayName) ||
    str(cfg.property_name) || str(cfg.propertyName) ||
    str(cfg.customer_name) || str(cfg.customerName) ||
    str(cfg.property_id) || str(cfg.propertyId) ||
    str(cfg.customer_id) || str(cfg.customerId);
  return readable ? `${typeLabel} — ${readable}` : `${typeLabel} (#${source.id})`;
}

/** Merge profond : préserve les sous-objets `format`/`display`/`period` non touchés par le patch. */
function deepMergeConfig(current: WidgetConfig, patch: Partial<WidgetConfig>): WidgetConfig {
  const next: WidgetConfig = { ...current, ...patch };

  if (patch.format !== undefined) {
    next.format = { ...(current.format ?? {}), ...(patch.format ?? {}) };
  }
  if (patch.display !== undefined) {
    next.display = { ...(current.display ?? {}), ...(patch.display ?? {}) };
  }
  if (patch.period !== undefined) {
    next.period = { ...(current.period ?? { mode: 'inherit' }), ...(patch.period ?? {}) } as WidgetConfig['period'];
  }

  return next;
}

export default function WidgetConfigDrawer({ clientId, widget, onChange, onClose }: WidgetConfigDrawerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('config');
  const [sources, setSources] = useState<DataSource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** Cache local des valeurs distinctes, clé `${data_source_id}:${dimension}` (Orion n'a pas react-query). */
  const [dimValues, setDimValues] = useState<Record<string, string[]>>({});
  /** Dimensions déjà demandées : évite de refetcher la même à chaque rendu (et à chaque frappe). */
  const requestedDims = useRef<Set<string>>(new Set());
  const [calcFields, setCalcFields] = useState<CalcField[]>([]);
  const [showCalcManager, setShowCalcManager] = useState(false);
  /** Incrémenté à la fermeture de l'overlay de gestion : force un refetch des champs calculés. */
  const [calcRefreshTick, setCalcRefreshTick] = useState(0);

  const cfg: WidgetConfig = widget?.config ?? {};
  /** Widgets de mise en page (divider, image, texte, période) : aucune donnée à configurer. */
  const isDataWidget = widget ? needsDataSource(widget.type) : false;

  const selectedSource = useMemo(
    () => sources.find((s) => String(s.id) === String(cfg.data_source_id)),
    [sources, cfg.data_source_id]
  );
  const sourceType = selectedSource?.type;

  // Charge les sources du client dès qu'un widget de données est sélectionné.
  useEffect(() => {
    if (!widget || !isDataWidget) return;
    let cancelled = false;
    setSourcesLoading(true);
    fetch(`/api/data-sources/clients/${clientId}/data-sources`, { credentials: 'include' })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        setSources(json?.success ? (json.data as DataSource[]) : []);
      })
      .catch(() => {
        if (!cancelled) setSources([]);
      })
      .finally(() => {
        if (!cancelled) setSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, widget?.id, isDataWidget]);

  // Recharge le catalogue métriques/dimensions/granularités à chaque changement de type de source.
  useEffect(() => {
    if (!sourceType || !isDataWidget) {
      setMetadata(null);
      return;
    }
    let cancelled = false;
    setMetadataLoading(true);
    fetch(`/api/query/metadata/${sourceType}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        setMetadata(json?.success ? (json.data as Metadata) : null);
      })
      .catch(() => {
        if (!cancelled) setMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setMetadataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceType, isDataWidget]);

  // Charge les champs calculés de la source sélectionnée (métriques/dimensions custom).
  // Refetch aussi quand l'overlay de gestion se ferme (`calcRefreshTick`).
  useEffect(() => {
    if (!isDataWidget || !cfg.data_source_id) {
      setCalcFields([]);
      return;
    }
    let cancelled = false;
    listFields(clientId, Number(cfg.data_source_id))
      .then((f) => {
        if (!cancelled) setCalcFields(f);
      })
      .catch(() => {
        if (!cancelled) setCalcFields([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, cfg.data_source_id, isDataWidget, calcRefreshTick]);

  /** Dimension de breakdown active : line_chart/area_chart avec dimension + granularité
   *  choisies (cf. resolveBreakdownKey côté rendu — le drawer approxime la même condition
   *  sur la config plutôt que sur les données, il ne les a pas encore chargées ici). */
  const breakdownDimension =
    (widget?.type === 'line_chart' || widget?.type === 'area_chart') && cfg.display?.granularity
      ? cfg.dimensions?.[0]
      : widget?.type === 'table'
        ? (cfg.display?.row_group_dimension as string | undefined)
        : undefined;

  // Dimensions filtrées, dédupliquées. Mémoïsé : sans ça, un nouveau tableau à chaque
  // rendu relancerait l'effet ci-dessous en boucle.
  // Le catalogue est requis pour distinguer une dimension d'une métrique (une métrique
  // se filtre en numérique : aucune valeur à proposer).
  const filteredDimensions = useMemo(() => {
    if (!metadata) return [] as string[];
    const isMetric = (field: string) => metadata.metrics.some((m) => m.key === field);
    return [
      ...new Set(
        [...(cfg.filters ?? []).map((f) => f.field), breakdownDimension]
          .filter((field): field is string => !!field && !isMetric(field)),
      ),
    ];
  }, [cfg.filters, breakdownDimension, metadata]);

  // Charge à la demande les valeurs connues de chaque dimension filtrée, une seule fois
  // par couple source/dimension. `fetchDimensionValues` ne throw jamais : un backend en
  // erreur laisse simplement la liste vide (saisie libre) sans casser le drawer.
  const dataSourceId = cfg.data_source_id;
  useEffect(() => {
    if (!dataSourceId) return;
    for (const dim of filteredDimensions) {
      const cacheKey = `${dataSourceId}:${dim}`;
      if (requestedDims.current.has(cacheKey)) continue;
      requestedDims.current.add(cacheKey);
      fetchDimensionValues(String(dataSourceId), dim).then((values) => {
        setDimValues((prev) => ({ ...prev, [cacheKey]: values }));
      });
    }
  }, [dataSourceId, filteredDimensions]);

  // Listes fusionnées catalogue + champs calculés, UNIQUEMENT pour les checkboxes
  // métriques/dimensions : les champs calculés restent hors filtres et drill-down
  // (selectableDimensions/filterFieldOptions/drillCandidates, calculés plus bas,
  // ne sont pas touchés). Déclarés avant l'early-return `!widget` pour respecter
  // les règles des Hooks (nombre de hooks stable entre rendus).
  // Dimensions non temporelles : source unique, réutilisée par le merge ci-dessous ET
  // par `selectableDimensions` (filtres/drill) plus bas — évite une double définition du filtre.
  const nonTimeDimensions = useMemo(
    () => (metadata?.dimensions ?? []).filter((d) => !d.time),
    [metadata],
  );
  // Champs calculés fusionnés aux natifs UNIQUEMENT dans les checkboxes métriques/dimensions.
  // Gaté sur `metadata` : sans catalogue de source, aucun champ n'est proposé (cohérence avec
  // le placeholder « Sélectionnez une source »).
  const mergedMetricOptions = useMemo<MergedField[]>(
    () =>
      metadata
        ? [
            ...metadata.metrics,
            ...calcFields
              .filter((f) => f.fieldType === 'metric')
              .map((f) => ({ key: f.fieldId, label: f.name, calculated: true })),
          ]
        : [],
    [metadata, calcFields],
  );
  const mergedDimensionOptions = useMemo<MergedField[]>(
    () =>
      metadata
        ? [
            ...nonTimeDimensions,
            ...calcFields
              .filter((f) => f.fieldType === 'dimension')
              .map((f) => ({ key: f.fieldId, label: f.name, calculated: true })),
          ]
        : [],
    [metadata, nonTimeDimensions, calcFields],
  );

  if (!widget) return null;

  /** Valeurs connues d'une dimension pour la source courante (vide tant que non chargées). */
  function valuesFor(field: string): string[] {
    return dimValues[`${dataSourceId}:${field}`] ?? [];
  }

  function patchConfig(partial: Partial<WidgetConfig>) {
    if (!widget) return;
    onChange(widget.id, deepMergeConfig(widget.config ?? {}, partial));
  }

  const breakdownValues = breakdownDimension ? valuesFor(breakdownDimension) : [];

  function isExcludedValue(value: string): boolean {
    return (cfg.filters ?? []).some(
      (f) => f.field === breakdownDimension && f.operator === 'exact' && f.mode === 'exclude' && f.value === value,
    );
  }

  function toggleExcludedValue(value: string) {
    const current = cfg.filters ?? [];
    if (isExcludedValue(value)) {
      patchConfig({
        filters: current.filter(
          (f) => !(f.field === breakdownDimension && f.operator === 'exact' && f.mode === 'exclude' && f.value === value),
        ),
      });
    } else {
      patchConfig({
        filters: [
          ...current,
          { field: breakdownDimension!, operator: 'exact', mode: 'exclude', value, target: 'dimension' },
        ],
      });
    }
  }

  /** Upload d'un fichier image : renseigne `display.url` avec l'URL servable renvoyée par l'API. */
  async function handleImageFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const url = await uploadImage(clientId, file);
      patchConfig({ display: { url } });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload impossible');
    } finally {
      setUploading(false);
    }
  }

  /** Champ numérique : un champ vidé efface la clé (`undefined`), il ne vaut ni `NaN` ni `0`. */
  function numberOrUndefined(raw: string): number | undefined {
    if (raw.trim() === '') return undefined;
    const value = Number(raw);
    return Number.isNaN(value) ? undefined : value;
  }

  function handleSourceChange(id: string) {
    // `row_group_dimension` référence une clé du catalogue de l'ANCIENNE source : la
    // vider explicitement évite qu'elle ne redevienne "valide" par coïncidence si la
    // nouvelle source expose une dimension de même clé mais de sens différent.
    patchConfig({
      data_source_id: id,
      metrics: [],
      dimensions: [],
      display: { row_group_dimension: undefined, row_group_metrics: [] },
    });
  }

  function toggleInList(list: string[] | undefined, key: string): string[] {
    const current = list ?? [];
    return current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
  }

  /**
   * Sur un graphique, l'axe X est SOIT le temps (granularité) SOIT une dimension —
   * jamais les deux : le backend renverrait le produit des deux (30 dates × 3 appareils)
   * et le widget tracerait une série unique sur un axe incohérent. Cocher une dimension
   * libère donc la granularité, et inversement.
   */
  function handleDimensionToggle(key: string) {
    const dimensions = toggleInList(cfg.dimensions, key);
    const clearsGranularity = isChart && dimensions.length > 0;
    patchConfig(clearsGranularity ? { dimensions, display: { granularity: '' } } : { dimensions });
  }

  function handleGranularityChange(granularity: string) {
    const clearsDimensions = isChart && !!granularity && (cfg.dimensions?.length ?? 0) > 0;
    patchConfig(clearsDimensions ? { display: { granularity }, dimensions: [] } : { display: { granularity } });
  }

  /** Le champ est-il une métrique (valeurs numériques) plutôt qu'une dimension ? */
  function isMetricField(field: string): boolean {
    return !!metadata?.metrics.some((m) => m.key === field);
  }

  function updateFilter(index: number, patch: Partial<WidgetFilter>) {
    const filters = [...(cfg.filters ?? [])];
    filters[index] = { ...filters[index], ...patch };
    patchConfig({ filters });
  }

  /**
   * Changer de champ peut invalider l'opérateur : `contains` n'existe pas sur une
   * métrique, `gt` n'existe pas sur une dimension. On réaligne sur l'égalité du
   * nouveau type plutôt que d'envoyer un opérateur refusé par le backend.
   */
  function changeFilterField(index: number, field: string) {
    const current = (cfg.filters ?? [])[index];
    const isMetric = isMetricField(field);
    const stillValid = operatorsFor(isMetric).some(
      (op) => op.operator === current?.operator && (op.mode ?? undefined) === (current?.mode ?? undefined),
    );
    updateFilter(index, {
      field,
      ...(stillValid
        ? { operator: current.operator, mode: current.mode }
        : { operator: defaultOperatorFor(isMetric), mode: undefined }),
      target: isMetric ? 'metric' : 'dimension',
    });
  }

  function addFilter() {
    // Défaut = 1er champ RÉELLEMENT filtrable (filterFieldOptions exclut les dimensions
    // temporelles). Utiliser metadata.dimensions[0] pouvait retomber sur `date` (time),
    // absente du menu → filtre `date = ''` invisible qui vidait le widget.
    const firstField = filterFieldOptions[0]?.key ?? '';
    const isMetric = isMetricField(firstField);
    const filters = [
      ...(cfg.filters ?? []),
      {
        field: firstField,
        operator: defaultOperatorFor(isMetric),
        value: '',
        target: (isMetric ? 'metric' : 'dimension') as WidgetFilter['target'],
      },
    ];
    patchConfig({ filters });
  }

  function removeFilter(index: number) {
    const filters = (cfg.filters ?? []).filter((_, i) => i !== index);
    patchConfig({ filters });
  }

  // Les dimensions temporelles (`date`, `yearMonth`, `week`, `hour`…) ne sont jamais
  // proposées : l'axe temps se pilote uniquement par la granularité. Les exposer en
  // parallèle créait deux contrôles temps concurrents (ex. granularité Jour + dimension
  // `week` → n° de semaine bruts sur l'axe).
  const selectableDimensions = nonTimeDimensions;
  const filterFieldOptions = [...selectableDimensions, ...(metadata?.metrics ?? [])];

  // Drill-down : les niveaux de descente sont des dimensions NON temporelles (l'axe
  // temps se pilote par la granularité) et différentes de la dimension principale,
  // qui sert déjà de premier filtre au clic (cf. `useDrillDown`).
  const supportsDrillDown = DRILLDOWN_WIDGETS.includes(widget.type);
  const drillDimensions = (cfg.display?.drill_dimensions as string[] | undefined) ?? [];
  const primaryDimension = cfg.dimensions?.[0];
  const drillCandidates = selectableDimensions.filter((d) => d.key !== primaryDimension);
  const nextDrillCandidate = drillCandidates.find((d) => !drillDimensions.includes(d.key));

  function setDrillDimensions(next: string[]) {
    // Exclusivité avec le regroupement hiérarchique : sans ça, un widget avec les deux
    // champs renseignés masquerait les deux contrôles sans échappatoire dans le drawer.
    patchConfig({ display: { drill_dimensions: next, ...(next.length > 0 ? { row_group_dimension: undefined } : {}) } });
  }

  function addDrillLevel() {
    if (!nextDrillCandidate) return;
    setDrillDimensions([...drillDimensions, nextDrillCandidate.key]);
  }

  function updateDrillLevel(index: number, key: string) {
    const next = [...drillDimensions];
    next[index] = key;
    setDrillDimensions(next);
  }

  function removeDrillLevel(index: number) {
    setDrillDimensions(drillDimensions.filter((_, i) => i !== index));
  }

  /** Options d'un niveau : les dimensions libres + celle déjà choisie pour ce niveau. */
  function drillOptionsFor(index: number): CatalogField[] {
    return drillCandidates.filter(
      (d) => !drillDimensions.includes(d.key) || d.key === drillDimensions[index],
    );
  }

  // Regroupement hiérarchique (widget table uniquement) : dimension secondaire nichée sous
  // chaque ligne de la dimension primaire (cf. TableWidget). Mutuellement exclusif avec le
  // drill-down — le drawer n'autorise qu'un des deux mécanismes à la fois.
  const supportsRowGroup = widget.type === 'table';
  const rowGroupDimension = (cfg.display?.row_group_dimension as string | undefined) ?? '';
  const rowGroupMetrics = (cfg.display?.row_group_metrics as string[] | undefined) ?? [];
  const rowGroupCandidates = drillCandidates;

  function setRowGroupDimension(dim: string) {
    // Exclusivité avec le drill-down : sans ça, un widget avec les deux champs renseignés
    // masquerait les deux contrôles sans échappatoire dans le drawer.
    patchConfig({
      display: {
        row_group_dimension: dim || undefined,
        row_group_metrics: [],
        ...(dim ? { drill_dimensions: [] } : {}),
      },
    });
  }

  function toggleRowGroupMetric(key: string) {
    patchConfig({ display: { row_group_metrics: toggleInList(rowGroupMetrics, key) } });
  }

  const periodMode = cfg.period?.mode ?? 'inherit';
  const isTimeSeries = TIME_SERIES_WIDGETS.includes(widget.type);
  const isChart = EXCLUSIVE_TIME_OR_DIMENSION_WIDGETS.includes(widget.type);
  const granularities = metadata?.granularities ?? [];

  /** Libellé FR d'une métrique (repli sur la clé tant que le catalogue n'est pas chargé). */
  function metricLabelFor(key: string): string {
    return metadata?.metrics.find((m) => m.key === key)?.label ?? key;
  }

  return (
    <aside className="flex w-[360px] shrink-0 flex-col overflow-hidden border-l border-[#E2E4ED] bg-white shadow-[-2px_0_8px_rgba(27,37,89,0.04)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-[#E2E4ED] px-5 py-4">
        <div className="min-w-0">
          <div className="font-semibold text-[#1B2559]">Configurer le widget</div>
          <div className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-wider text-[#8B8FA7]">
            {widget.type}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-[#8B8FA7] transition-colors hover:bg-[#F4F5FA] hover:text-[#1B2559]"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>

      {/* Tabs — segmented control */}
      <div className="shrink-0 px-5 pb-1 pt-3">
        <div className="flex gap-1 rounded-lg bg-[#F4F5FA] p-1">
          {(['config', 'style'] as Tab[]).map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={
                  active
                    ? 'flex-1 rounded-md bg-white py-1.5 text-sm font-medium text-[#6366F1] shadow-sm transition-colors'
                    : 'flex-1 rounded-md py-1.5 text-sm font-medium text-[#8B8FA7] transition-colors hover:text-[#1B2559]'
                }
              >
                {tab === 'config' ? 'Configuration' : 'Style'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {activeTab === 'config' ? (
          <div className="flex flex-col gap-5">
            {widget.type === 'divider' && (
              <p className="text-sm text-[#8B8FA7]">
                Le séparateur n'a pas de données à configurer.
              </p>
            )}

            {widget.type === 'date_range_display' && (
              <p className="text-sm text-[#8B8FA7]">
                Ce bloc affiche la période du rapport : rien à configurer ici.
              </p>
            )}

            {isDataWidget && (
              <>
                {/* Source */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-[#5F6477]">Source de données</label>
                  <select
                    className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                    value={cfg.data_source_id ?? ''}
                    onChange={(e) => handleSourceChange(e.target.value)}
                    disabled={sourcesLoading}
                  >
                    <option value="" disabled>
                      {sourcesLoading ? 'Chargement…' : 'Sélectionner une source'}
                    </option>
                    {sources.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {sourceLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Métriques */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-[#5F6477]">Métriques</label>
                  <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-[#E2E4ED] bg-[#F4F5FA] p-2">
                    {metadataLoading && <span className="text-xs text-[#8B8FA7]">Chargement…</span>}
                    {!metadataLoading && !metadata && (
                      <span className="text-xs text-[#8B8FA7]">Sélectionnez une source</span>
                    )}
                    {mergedMetricOptions.map((m) => (
                      <label key={m.key} className="flex items-center gap-2 text-sm text-[#1B2559]">
                        <input
                          type="checkbox"
                          checked={(cfg.metrics ?? []).includes(m.key)}
                          onChange={() => patchConfig({ metrics: toggleInList(cfg.metrics, m.key) })}
                        />
                        {m.calculated && (
                          <span className="mr-1 rounded bg-[#6366F1]/10 px-1 text-[10px] font-semibold text-[#6366F1]">
                            fx
                          </span>
                        )}
                        {m.label ?? m.key}
                      </label>
                    ))}
                  </div>
                  {widget.type === 'map' && (
                    <p className="text-[11px] text-[#8B8FA7]">
                      1re métrique = taille des bulles · 2e métrique = couleur (intensité).
                    </p>
                  )}
                </div>

                {/* Granularité — contrôle temps principal des graphiques, donc placé
                    juste après les métriques et avant les dimensions (qui, elles, ne
                    servent plus qu'à découper par pays/appareil/campagne…). */}
                {!!granularities.length && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]">
                      {isTimeSeries ? 'Axe temps (granularité)' : 'Granularité'}
                    </label>
                    <select
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={(cfg.display?.granularity as string | undefined) ?? ''}
                      onChange={(e) => handleGranularityChange(e.target.value)}
                    >
                      <option value="">Aucune</option>
                      {granularities.map((g) => (
                        <option key={g} value={g}>
                          {GRANULARITY_LABELS[g] ?? g}
                        </option>
                      ))}
                    </select>
                    {isTimeSeries && (
                      <span className="text-[11px] text-[#8B8FA7]">
                        Détermine les points de l'axe horizontal.
                      </span>
                    )}
                  </div>
                )}

                {/* Dimensions */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-[#5F6477]">Dimensions</label>
                  <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-[#E2E4ED] bg-[#F4F5FA] p-2">
                    {metadataLoading && <span className="text-xs text-[#8B8FA7]">Chargement…</span>}
                    {!metadataLoading && !metadata && (
                      <span className="text-xs text-[#8B8FA7]">Sélectionnez une source</span>
                    )}
                    {mergedDimensionOptions.map((d) => (
                      <label key={d.key} className="flex items-center gap-2 text-sm text-[#1B2559]">
                        <input
                          type="checkbox"
                          checked={(cfg.dimensions ?? []).includes(d.key)}
                          onChange={() => handleDimensionToggle(d.key)}
                        />
                        {d.calculated && (
                          <span className="mr-1 rounded bg-[#6366F1]/10 px-1 text-[10px] font-semibold text-[#6366F1]">
                            fx
                          </span>
                        )}
                        {d.label ?? d.key}
                      </label>
                    ))}
                  </div>
                  {cfg.data_source_id && (
                    <button
                      type="button"
                      onClick={() => setShowCalcManager(true)}
                      className="flex items-center gap-1 text-xs text-[#6366F1] hover:underline"
                    >
                      <span className="rounded bg-[#6366F1]/10 px-1 text-[10px] font-semibold">fx</span>
                      Gérer les champs calculés
                    </button>
                  )}
                </div>

                {supportsRowGroup && drillDimensions.length === 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]">Regrouper par</label>
                    <select
                      aria-label="Dimension de regroupement"
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={rowGroupDimension}
                      onChange={(e) => setRowGroupDimension(e.target.value)}
                    >
                      <option value="">Aucun (tableau plat)</option>
                      {rowGroupCandidates.map((d) => (
                        <option key={d.key} value={d.key}>{d.label ?? d.key}</option>
                      ))}
                    </select>
                    {rowGroupDimension && (
                      <div className="mt-1 flex flex-col gap-1.5">
                        <label className="text-xs text-[#5F6477]">Métriques à segmenter par sous-ligne</label>
                        <div className="flex max-h-32 flex-col gap-1 overflow-y-auto rounded-md border border-[#E2E4ED] bg-[#F4F5FA] p-2">
                          {(cfg.metrics ?? []).map((m) => (
                            <label key={m} className="flex items-center gap-2 text-sm text-[#1B2559]">
                              <input
                                type="checkbox"
                                checked={rowGroupMetrics.includes(m)}
                                onChange={() => toggleRowGroupMetric(m)}
                              />
                              {metricLabelFor(m)}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {breakdownDimension && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]">Valeurs affichées</label>
                    <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-[#E2E4ED] bg-[#F4F5FA] p-2">
                      {breakdownValues.length === 0 && (
                        <span className="text-xs text-[#8B8FA7]">Chargement…</span>
                      )}
                      {breakdownValues.map((value) => (
                        <label key={value} className="flex items-center gap-2 text-sm text-[#1B2559]">
                          <input
                            type="checkbox"
                            checked={!isExcludedValue(value)}
                            onChange={() => toggleExcludedValue(value)}
                          />
                          {value}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Périmètre de la carte — noms de carte attendus par `resolveMapName`. */}
                {widget.type === 'map' && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]" htmlFor="map-scope">
                      Périmètre de la carte
                    </label>
                    <select
                      id="map-scope"
                      aria-label="Périmètre de la carte"
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={(cfg.display?.map_scope as string | undefined) ?? 'world'}
                      onChange={(e) => patchConfig({ display: { map_scope: e.target.value } })}
                    >
                      {MAP_SCOPES.map((scope) => (
                        <option key={scope} value={scope}>
                          {MAP_SCOPE_LABELS[scope]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Type de localisation Google Ads — filtre geographic_view.location_type. */}
                {widget.type === 'map' && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]" htmlFor="map-location-type">
                      Type de localisation
                    </label>
                    <select
                      id="map-location-type"
                      aria-label="Type de localisation"
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={(cfg.display?.location_type as string | undefined) ?? 'all'}
                      onChange={(e) => patchConfig({ display: { location_type: e.target.value } })}
                    >
                      <option value="all">Présence ou intérêt</option>
                      <option value="LOCATION_OF_PRESENCE">Présence uniquement</option>
                    </select>
                  </div>
                )}

                {/* Fond de carte Leaflet — voir `widgets/basemaps.ts`. */}
                {widget.type === 'map' && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]" htmlFor="map-basemap">
                      Fond de carte
                    </label>
                    <select
                      id="map-basemap"
                      aria-label="Fond de carte"
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={(cfg.display?.basemap as string | undefined) ?? DEFAULT_BASEMAP}
                      onChange={(e) => patchConfig({ display: { basemap: e.target.value } })}
                    >
                      {BASEMAPS.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Affichage des villes : bulles (défaut) ou carte de chaleur (Leaflet.heat). */}
                {widget.type === 'map' && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-[#5F6477]" htmlFor="map-city-layer">
                      Affichage des villes
                    </label>
                    <select
                      id="map-city-layer"
                      aria-label="Affichage des villes"
                      className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                      value={(cfg.display?.map_city_layer as string | undefined) ?? 'bubbles'}
                      onChange={(e) =>
                        patchConfig({ display: { map_city_layer: e.target.value as 'bubbles' | 'heat' } })
                      }
                    >
                      <option value="bubbles">Bulles</option>
                      <option value="heat">Carte de chaleur</option>
                    </select>
                  </div>
                )}

                {widget.type === 'map' && (
                  <label className="flex items-center gap-2 text-sm text-[#1B2559]">
                    <input
                      type="checkbox"
                      aria-label="Afficher les valeurs sur la carte"
                      checked={(cfg.display?.map_show_values as boolean | undefined) ?? false}
                      onChange={(e) => patchConfig({ display: { map_show_values: e.target.checked } })}
                    />
                    Afficher les valeurs sur la carte
                  </label>
                )}

                {widget.type === 'map' && (
                  <label className="flex items-center gap-2 text-sm text-[#1B2559]">
                    <input
                      type="checkbox"
                      aria-label="Regrouper les bulles au dézoom"
                      checked={(cfg.display?.map_cluster as boolean | undefined) ?? true}
                      onChange={(e) => patchConfig({ display: { map_cluster: e.target.checked } })}
                    />
                    Regrouper les bulles au dézoom
                  </label>
                )}

                {/* Niveaux de drill-down — sans ce contrôle, le drill-down reste inaccessible. */}
                {supportsDrillDown && !rowGroupDimension && (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-[#5F6477]">Niveaux de drill-down</label>
                      <button
                        type="button"
                        onClick={addDrillLevel}
                        disabled={!nextDrillCandidate}
                        className="flex items-center gap-1 text-xs font-medium text-[#6366F1] hover:text-[#4F46E5] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Plus size={12} /> Ajouter un niveau
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                      {drillDimensions.map((dim, index) => (
                        <div key={`${dim}-${index}`} className="flex items-center gap-1.5">
                          <span className="w-4 shrink-0 text-xs text-[#8B8FA7]">{index + 1}.</span>
                          <select
                            aria-label={`Niveau de drill-down ${index + 1}`}
                            className="min-w-0 flex-1 rounded-md border border-[#E2E4ED] bg-white px-1.5 py-1 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                            value={dim}
                            onChange={(e) => updateDrillLevel(index, e.target.value)}
                          >
                            {drillOptionsFor(index).map((d) => (
                              <option key={d.key} value={d.key}>
                                {d.label ?? d.key}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => removeDrillLevel(index)}
                            className="shrink-0 rounded-md p-1 text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#EF4444]"
                            aria-label="Supprimer ce niveau"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                      {drillDimensions.length === 0 && (
                        <span className="text-xs text-[#8B8FA7]">
                          Aucun niveau : le clic sur le widget reste inerte.
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Filtres */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-[#5F6477]">Filtres</label>
                    <button
                      type="button"
                      onClick={addFilter}
                      disabled={!metadata}
                      className="flex items-center gap-1 text-xs font-medium text-[#6366F1] hover:text-[#4F46E5] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus size={12} /> Ajouter
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {(cfg.filters ?? []).map((filter, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <select
                          aria-label={`Champ du filtre ${index + 1}`}
                          className="min-w-0 flex-1 rounded-md border border-[#E2E4ED] bg-white px-1.5 py-1 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                          value={filter.field}
                          onChange={(e) => changeFilterField(index, e.target.value)}
                        >
                          {filterFieldOptions.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label ?? f.key}
                            </option>
                          ))}
                        </select>
                        {/* Opérateurs restreints à ceux qu'accepte le moteur (cf. `filterOperators`,
                            miroir d'ALLOWED_OPS) et au type du champ : tout le reste part en 400. */}
                        <select
                          aria-label={`Opérateur du filtre ${index + 1}`}
                          className="min-w-0 flex-1 rounded-md border border-[#E2E4ED] bg-white px-1 py-1 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                          value={operatorOptionValue(filter.operator, filter.mode)}
                          onChange={(e) => updateFilter(index, resolveOperatorOption(e.target.value))}
                        >
                          {operatorsFor(isMetricField(filter.field)).map((op) => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                        {/* Une dimension se saisit au texte, avec les valeurs connues en suggestion
                            (datalist) ; une métrique se filtre en numérique. */}
                        <input
                          type={isMetricField(filter.field) ? 'number' : 'text'}
                          list={isMetricField(filter.field) ? undefined : `dv-${index}`}
                          aria-label={`Valeur du filtre ${index + 1}`}
                          className="min-w-0 flex-1 rounded-md border border-[#E2E4ED] bg-white px-1.5 py-1 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                          value={String(filter.value ?? '')}
                          onChange={(e) =>
                            updateFilter(index, {
                              value: isMetricField(filter.field)
                                ? numberOrUndefined(e.target.value)
                                : e.target.value,
                            })
                          }
                        />
                        {!isMetricField(filter.field) && (
                          <datalist id={`dv-${index}`}>
                            {valuesFor(filter.field).map((v) => (
                              <option key={v} value={v} />
                            ))}
                          </datalist>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFilter(index)}
                          className="shrink-0 rounded-md p-1 text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#EF4444]"
                          aria-label="Supprimer ce filtre"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                    {(cfg.filters ?? []).length === 0 && (
                      <span className="text-xs text-[#8B8FA7]">Aucun filtre</span>
                    )}
                  </div>
                </div>

                {/* Période */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-[#5F6477]">Période</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 text-sm text-[#1B2559]">
                      <input
                        type="radio"
                        name="period-mode"
                        checked={periodMode === 'inherit'}
                        onChange={() => patchConfig({ period: { mode: 'inherit' } })}
                      />
                      Hérite du rapport
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-[#1B2559]">
                      <input
                        type="radio"
                        name="period-mode"
                        checked={periodMode === 'custom'}
                        onChange={() =>
                          patchConfig({
                            period: { mode: 'custom', start: cfg.period?.start, end: cfg.period?.end },
                          })
                        }
                      />
                      Personnalisée
                    </label>
                  </div>
                  {periodMode === 'custom' && (
                    <div className="mt-1 flex gap-2">
                      <input
                        type="date"
                        className="flex-1 rounded-md border border-[#E2E4ED] bg-white px-2 py-1.5 text-sm text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                        value={cfg.period?.start ?? ''}
                        onChange={(e) =>
                          patchConfig({ period: { mode: 'custom', start: e.target.value, end: cfg.period?.end } })
                        }
                      />
                      <input
                        type="date"
                        className="flex-1 rounded-md border border-[#E2E4ED] bg-white px-2 py-1.5 text-sm text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
                        value={cfg.period?.end ?? ''}
                        onChange={(e) =>
                          patchConfig({ period: { mode: 'custom', start: cfg.period?.start, end: e.target.value } })
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Comparaison */}
                <label className="flex items-center gap-2 text-sm text-[#1B2559]">
                  <input
                    type="checkbox"
                    checked={!!cfg.compare}
                    onChange={(e) => patchConfig({ compare: e.target.checked })}
                  />
                  Comparer à la période précédente
                </label>

              </>
            )}

            {/* IMAGE — display.url / alt / object_fit, lus par ImageBlockWidget. */}
            {isImageWidget(widget.type) && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="img-url" className="text-xs text-[#5F6477]">
                    URL de l'image
                  </label>
                  <input
                    id="img-url"
                    type="url"
                    className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                    value={cfg.display?.url ?? ''}
                    onChange={(e) => patchConfig({ display: { url: e.target.value } })}
                    placeholder="https://… ou /api/uploads/files/…"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="img-file" className="text-xs text-[#5F6477]">
                    Importer un fichier
                  </label>
                  <input
                    id="img-file"
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
                    className="text-xs text-[#1B2559] file:mr-2 file:rounded-md file:border-0 file:bg-[#F4F5FA] file:px-2 file:py-1 file:text-xs file:text-[#1B2559]"
                    onChange={handleImageFile}
                    disabled={uploading}
                  />
                  {uploading && <span className="text-[11px] text-[#8B8FA7]">Envoi en cours…</span>}
                  {uploadError && (
                    <span role="alert" className="text-[11px] text-[#EF4444]">
                      {uploadError}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="img-alt" className="text-xs text-[#5F6477]">
                    Texte alternatif
                  </label>
                  <input
                    id="img-alt"
                    type="text"
                    className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                    value={cfg.display?.alt ?? ''}
                    onChange={(e) => patchConfig({ display: { alt: e.target.value } })}
                    placeholder="Description de l'image"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="img-fit" className="text-xs text-[#5F6477]">
                    Cadrage
                  </label>
                  <select
                    id="img-fit"
                    className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                    value={cfg.display?.object_fit ?? 'cover'}
                    onChange={(e) =>
                      patchConfig({ display: { object_fit: e.target.value as 'cover' | 'contain' } })
                    }
                  >
                    <option value="cover">Remplir le cadre (rogné)</option>
                    <option value="contain">Image entière (marges)</option>
                  </select>
                </div>
              </div>
            )}

            {/* TEXTE ENRICHI — l'édition se fait au clavier dans le bloc (cf. tâche 9). */}
            {isRichTextWidget(widget.type) && (
              <p className="text-sm text-[#8B8FA7]">
                Le contenu s'édite directement dans le bloc, sur le canevas, en mode Édition.
              </p>
            )}

            {/* OBJECTIF — gauge & progress_bar (display.target, lu par resolveTarget). */}
            {needsTarget(widget.type) && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="w-target" className="text-xs text-[#5F6477]">
                    Objectif
                  </label>
                  <input
                    id="w-target"
                    type="number"
                    min={0}
                    className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                    value={cfg.display?.target ?? ''}
                    onChange={(e) => patchConfig({ display: { target: numberOrUndefined(e.target.value) } })}
                  />
                  <span className="text-[11px] text-[#8B8FA7]">
                    Sans objectif, le widget affiche « À configurer ».
                  </span>
                </div>

                {widget.type === 'gauge' && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="w-min" className="text-xs text-[#5F6477]">
                        Minimum
                      </label>
                      {/* `0` est une valeur légitime : ne jamais écrire `Number(v) || undefined` ici. */}
                      <input
                        id="w-min"
                        type="number"
                        className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                        value={cfg.display?.min ?? 0}
                        onChange={(e) => patchConfig({ display: { min: numberOrUndefined(e.target.value) } })}
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-[#1B2559]">
                      <input
                        id="w-pointer"
                        type="checkbox"
                        checked={cfg.display?.show_pointer ?? true}
                        onChange={(e) => patchConfig({ display: { show_pointer: e.target.checked } })}
                      />
                      Aiguille
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[#1B2559]">
                      <input
                        id="w-show-target"
                        type="checkbox"
                        checked={cfg.display?.show_target ?? true}
                        onChange={(e) => patchConfig({ display: { show_target: e.target.checked } })}
                      />
                      Afficher la cible
                    </label>
                  </>
                )}
              </div>
            )}

            {/* TOP-N — display.limit, lu par TopList, Table, Bar, Donut, Treemap, Map. */}
            {hasTopN(widget.type) && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="w-limit" className="text-xs text-[#5F6477]">
                  Nombre de lignes
                </label>
                <input
                  id="w-limit"
                  type="number"
                  min={1}
                  max={50}
                  className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                  value={cfg.display?.limit ?? ''}
                  onChange={(e) => patchConfig({ display: { limit: numberOrUndefined(e.target.value) } })}
                  placeholder="10"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Titre */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-[#5F6477]">Titre</label>
              <input
                type="text"
                className="rounded-md border border-[#E2E4ED] bg-white h-7 px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
                value={cfg.title ?? ''}
                onChange={(e) => patchConfig({ title: e.target.value })}
                placeholder="Titre du widget"
              />
            </div>

            {/* Onglet Style unifié, monté par type via widgetStyleCaps (non-chart ET charts). */}
            <StyleTab
              widget={widget}
              cfg={cfg}
              patchConfig={patchConfig}
              metricLabel={metricLabelFor}
            />
          </div>
        )}
      </div>

      {showCalcManager && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
          onClick={() => {
            setShowCalcManager(false);
            setCalcRefreshTick((t) => t + 1);
          }}
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#1B2559]">Champs calculés</h4>
              <button
                type="button"
                onClick={() => {
                  setShowCalcManager(false);
                  setCalcRefreshTick((t) => t + 1);
                }}
                className="text-[#8B8FA7] hover:text-[#1B2559]"
              >
                ✕
              </button>
            </div>
            <CalculatedFieldsManager
              projectId={clientId}
              initialSourceId={cfg.data_source_id ? Number(cfg.data_source_id) : undefined}
            />
          </div>
        </div>
      )}
    </aside>
  );
}
